import { useState, type ReactNode } from "react";
import { ChevronDown, Code2, Download, ImageIcon, MousePointerClick, RotateCcw, Scissors, Wand2 } from "lucide-react";
import { Alert, Badge, Button, Card, Field, Input, Select, Textarea } from "@/components/ui";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { useGenerateHtml, useRenderImage } from "../hooks";
import { PreviewPane, type Zoom } from "../components/PreviewPane";
import { AiAssistPanel, type Selection } from "../components/AiAssistPanel";
import { fragmentAt } from "../selectMode";
import type { FrameToParent } from "../selectMode";

const ZOOM_OPTIONS: Array<{ value: Zoom; label: string }> = [
  { value: "fit", label: "适应" },
  { value: 0.4, label: "40%" },
  { value: 0.6, label: "60%" },
  { value: 0.8, label: "80%" },
  { value: 1, label: "100%" },
];

const DPI_OPTIONS = [
  { value: 96, label: "96 (网页)" },
  { value: 120, label: "120 (推荐)" },
  { value: 150, label: "150" },
  { value: 200, label: "200 (打印)" },
];

export function TemplatePage() {
  const [prompt, setPrompt] = useState("");
  const [widthCm, setWidthCm] = useState(40);
  const [heightCm, setHeightCm] = useState(60);
  const [html, setHtml] = useState("");
  const [image, setImage] = useState<{ b64: string; size: string; dpi: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [dpi, setDpi] = useState(120);
  const [selectMode, setSelectMode] = useState(false);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [showSource, setShowSource] = useState(false);

  const generate = useGenerateHtml();
  const render = useRenderImage();

  const handleGenerate = async () => {
    setError(null);
    setImage(null);
    try {
      const resp = await generate.mutateAsync({
        text: prompt.trim(),
        width_cm: widthCm,
        height_cm: heightCm,
      });
      if (resp.success && resp.html) {
        setHtml(resp.html);
        setSelection(null);
      } else {
        setError(resp.error || "生成失败");
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? `生成失败 (${e.status})` :
        e instanceof DOMException && e.name === "AbortError" ? "生成超时, 请稍后重试" :
        "网络错误",
      );
    }
  };

  const handleRegenerate = () => {
    if (!window.confirm("放弃当前编辑, 重新生成?")) return;
    void handleGenerate();
  };

  const handleRender = async () => {
    if (!html) return;
    setError(null);
    try {
      const resp = await render.mutateAsync({
        html,
        width_cm: widthCm,
        height_cm: heightCm,
        dpi,
      });
      const b64 = resp.images?.[0]?.b64_json;
      if (resp.success && b64) {
        setImage({ b64, size: resp.size || "", dpi });
      } else {
        setError(resp.error || "渲染失败");
      }
    } catch (e) {
      setError(
        e instanceof ApiError ? `渲染失败 (${e.status})` :
        e instanceof DOMException && e.name === "AbortError" ? "渲染超时 (>120s)" :
        "网络错误",
      );
    }
  };

  const downloadImage = () => {
    if (!image) return;
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${image.b64}`;
    a.download = `template_${widthCm}x${heightCm}cm_${image.dpi}dpi_${Date.now()}.png`;
    a.click();
  };

  // iframe 选中元素 → 用源码里对应元素作为片段 (不含运行时高亮 class)
  const handleSelect = (sel: FrameToParent) => {
    const fragment = fragmentAt(html, sel.path) ?? sel.html;
    setSelection({ path: sel.path, fragment });
  };

  const pxSize = `${Math.round((widthCm * 96) / 2.54)}×${Math.round((heightCm * 96) / 2.54)}px`;
  const sizeBadge = `${widthCm} × ${heightCm} cm`;

  return (
    <div className="px-3 py-4 md:px-4 md:py-6 lg:h-full">
      <h1 className="sr-only">模板设计</h1>
      <div className="grid gap-4 lg:h-full lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]">
        {/* 左侧: 尺寸 → 描述 → 生成 → 渲染, HTML 源码收在「高级」里 */}
        <div className="space-y-4 lg:min-h-0 lg:overflow-y-auto lg:pb-1 lg:pr-1">
          <Card>
            <StepHeader step={1} title="尺寸" aside={<span className="text-caption tabular-nums text-muted-foreground">{pxSize} @96dpi</span>} />
            <div className="grid grid-cols-2 gap-3 px-4 pb-4 pt-3">
              <Field label="宽 (cm)">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={widthCm}
                  onChange={(e) => setWidthCm(Number(e.target.value) || 0)}
                  min={5}
                  max={300}
                />
              </Field>
              <Field label="高 (cm)">
                <Input
                  type="number"
                  inputMode="decimal"
                  value={heightCm}
                  onChange={(e) => setHeightCm(Number(e.target.value) || 0)}
                  min={5}
                  max={300}
                />
              </Field>
            </div>
          </Card>

          <Card>
            <StepHeader step={2} title="需求描述" />
            <div className="space-y-3 px-4 pb-4 pt-3">
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                aria-label="需求描述"
                placeholder="例如: 一份夜市烧烤摊菜单, 红黄色调, 顶部大标题, 中间分类展示菜品和价格, 底部留电话"
                className="min-h-[120px] resize-y"
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleGenerate}
                  disabled={!prompt.trim()}
                  loading={generate.isPending}
                  icon={<Wand2 />}
                  className="flex-1"
                >
                  生成 HTML
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRegenerate}
                  disabled={generate.isPending || !prompt.trim() || !html}
                  title="放弃当前编辑, 用描述重新生成"
                  icon={<RotateCcw />}
                >
                  重新生成
                </Button>
              </div>
              <p className="text-caption text-muted-foreground">
                AI 生成网页 → 在预览里拖选元素让 AI 局部修改 → 渲染为高清图片
              </p>
            </div>
          </Card>

          <Card>
            <StepHeader step={3} title="渲染为图片" />
            <div className="space-y-3 px-4 pb-4 pt-3">
              <div className="flex items-end gap-2">
                <Field label="DPI" className="w-36 shrink-0">
                  <Select value={dpi} onChange={(e) => setDpi(Number(e.target.value))}>
                    {DPI_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Select>
                </Field>
                <Button
                  variant="outline"
                  onClick={handleRender}
                  disabled={!html}
                  loading={render.isPending}
                  icon={<ImageIcon />}
                  className="flex-1"
                >
                  渲染为图片
                </Button>
              </div>

              {image && (
                <div className="space-y-2 rounded-lg border p-2">
                  <img
                    src={`data:image/png;base64,${image.b64}`}
                    alt="渲染结果"
                    className="mx-auto max-h-48 max-w-full rounded"
                  />
                  <div className="flex items-center justify-between gap-2 px-1">
                    <span className="text-caption tabular-nums text-muted-foreground">
                      {image.size} @{image.dpi}dpi
                    </span>
                    <Button size="sm" onClick={downloadImage} icon={<Download />}>
                      下载
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSource((v) => !v)}
              aria-expanded={showSource}
              aria-controls="template-source"
              className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              <Code2 aria-hidden className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1">高级: 编辑 HTML</span>
              {html && <span className="text-caption tabular-nums text-muted-foreground">{html.length} 字符</span>}
              <ChevronDown
                aria-hidden
                className={cn("h-4 w-4 text-muted-foreground transition-transform", showSource && "rotate-180")}
              />
            </button>
            {showSource && (
              <div id="template-source" className="border-t p-3">
                <Textarea
                  value={html}
                  onChange={(e) => {
                    setHtml(e.target.value);
                    setSelection(null); // 手改源码后选中路径可能失效
                  }}
                  rows={12}
                  aria-label="HTML 源码"
                  spellCheck={false}
                  placeholder="LLM 生成后这里会显示 HTML 源码, 可手动调整"
                  className="resize-y font-mono text-xs leading-5 md:text-xs"
                />
              </div>
            )}
          </Card>

          {error && <Alert>{error}</Alert>}
        </div>

        {/* 右侧: 预览 (工具栏 + 棋盘格画布) + AI 修改助手 */}
        <div className="flex flex-col gap-4 lg:min-h-0">
          <Card className="flex min-h-[420px] flex-1 flex-col overflow-hidden lg:min-h-0">
            <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
              <h2 className="text-sm font-semibold">预览</h2>
              <Badge className="font-mono text-foreground">{sizeBadge}</Badge>
              <div className="ml-auto flex items-center gap-2">
                <label className="flex items-center gap-1.5 text-caption text-muted-foreground">
                  <span className="hidden sm:inline">缩放</span>
                  <Select
                    aria-label="缩放"
                    value={String(zoom)}
                    onChange={(e) => setZoom(e.target.value === "fit" ? "fit" : Number(e.target.value))}
                    disabled={!html}
                    className="w-[5.5rem]"
                  >
                    {ZOOM_OPTIONS.map((o) => (
                      <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                    ))}
                  </Select>
                </label>
                <Button
                  variant={selectMode ? "primary" : "outline"}
                  onClick={() => setSelectMode((v) => !v)}
                  disabled={!html}
                  aria-pressed={selectMode}
                  icon={<Scissors />}
                  title="在预览里拖出矩形或点击元素, 选中后 AI 只改该元素"
                >
                  拖选 AI 改
                </Button>
              </div>
            </div>
            {selectMode && (
              <div className="flex items-center gap-1.5 border-b bg-primary-soft px-4 py-1.5 text-caption font-medium text-primary">
                <MousePointerClick aria-hidden className="h-3.5 w-3.5" />
                在预览里拖出矩形或点击元素
              </div>
            )}
            <div className="h-[420px] p-3 lg:h-auto lg:min-h-0 lg:flex-1">
              <PreviewPane
                html={html}
                widthCm={widthCm}
                heightCm={heightCm}
                zoom={zoom}
                selectMode={selectMode}
                selectedPath={selection?.path ?? null}
                onSelect={handleSelect}
              />
            </div>
          </Card>

          {html && (
            <AiAssistPanel
              html={html}
              prompt={prompt}
              widthCm={widthCm}
              heightCm={heightCm}
              selection={selection}
              onClearSelection={() => setSelection(null)}
              onWholePageUpdated={(next, merged) => {
                setHtml(next);
                setPrompt(merged);
                setSelection(null);
                setImage(null);
              }}
              onFragmentUpdated={(next, fragment) => {
                setHtml(next);
                setSelection((s) => (s ? { ...s, fragment } : s));
                setImage(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StepHeader({ step, title, aside }: { step: number; title: string; aside?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-3.5">
      <span
        aria-hidden
        className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground"
      >
        {step}
      </span>
      <h2 className="flex-1 text-sm font-semibold">{title}</h2>
      {aside}
    </div>
  );
}
